import { useEffect, useState, type FormEvent } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useCreateEquipment, usePatchEquipment, useZones } from '@/lib/queries';
import type { Equipment, EquipmentStatus } from '@/lib/types';

type Props =
  | { open: boolean; onOpenChange: (next: boolean) => void; mode: 'create'; onCreated?: (id: string) => void }
  | { open: boolean; onOpenChange: (next: boolean) => void; mode: 'edit'; equipment: Equipment };

export default function EquipmentForm(props: Props) {
  const { open, onOpenChange, mode } = props;
  const create = useCreateEquipment();
  const patch = usePatchEquipment(mode === 'edit' ? props.equipment.id : '');
  const zonesQ = useZones();

  const initial =
    mode === 'edit'
      ? {
          name: props.equipment.name,
          category: props.equipment.category,
          brand: props.equipment.brand ?? '',
          model: props.equipment.model ?? '',
          serialNumber: props.equipment.serialNumber ?? '',
          purchaseDate: props.equipment.purchaseDate ?? '',
          storageZoneId: props.equipment.storageZoneId ?? '',
          notes: props.equipment.notes ?? '',
          status: props.equipment.status,
        }
      : {
          name: '',
          category: 'small-engine',
          brand: '',
          model: '',
          serialNumber: '',
          purchaseDate: '',
          storageZoneId: '',
          notes: '',
          status: 'active' as EquipmentStatus,
        };

  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.category.trim()) {
      setError('Name and category are required.');
      return;
    }
    const body = {
      name: form.name.trim(),
      category: form.category.trim(),
      brand: form.brand.trim() || null,
      model: form.model.trim() || null,
      serialNumber: form.serialNumber.trim() || null,
      purchaseDate: form.purchaseDate || null,
      storageZoneId: form.storageZoneId || null,
      notes: form.notes.trim() || null,
      status: form.status,
    };
    try {
      if (mode === 'create') {
        const result = await create.mutateAsync(body);
        props.onCreated?.(result.equipment.id);
        onOpenChange(false);
      } else {
        await patch.mutateAsync(body);
        onOpenChange(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'New equipment' : 'Edit equipment'}</SheetTitle>
          <SheetDescription>Make, model, serial, where it lives.</SheetDescription>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-4 pb-2" data-testid="equipment-form">
          <div className="space-y-1.5">
            <Label htmlFor="eq-name" className="font-mono text-[11px] uppercase tracking-wider">
              Name
            </Label>
            <Input
              id="eq-name"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
              className="h-11 text-base"
              data-testid="eq-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="eq-category" className="font-mono text-[11px] uppercase tracking-wider">
                Category
              </Label>
              <Input
                id="eq-category"
                required
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eq-status" className="font-mono text-[11px] uppercase tracking-wider">
                Status
              </Label>
              <select
                id="eq-status"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as EquipmentStatus }))}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="broken">broken</option>
                <option value="archived">archived</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="eq-brand" className="font-mono text-[11px] uppercase tracking-wider">
                Brand
              </Label>
              <Input
                id="eq-brand"
                value={form.brand}
                onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eq-model" className="font-mono text-[11px] uppercase tracking-wider">
                Model
              </Label>
              <Input
                id="eq-model"
                value={form.model}
                onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
                className="h-11"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="eq-serial" className="font-mono text-[11px] uppercase tracking-wider">
                Serial #
              </Label>
              <Input
                id="eq-serial"
                value={form.serialNumber}
                onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eq-purchase" className="font-mono text-[11px] uppercase tracking-wider">
                Purchased
              </Label>
              <Input
                id="eq-purchase"
                type="date"
                value={form.purchaseDate}
                onChange={(e) => setForm((f) => ({ ...f, purchaseDate: e.target.value }))}
                className="h-11"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eq-storage" className="font-mono text-[11px] uppercase tracking-wider">
              Storage zone
            </Label>
            <select
              id="eq-storage"
              value={form.storageZoneId}
              onChange={(e) => setForm((f) => ({ ...f, storageZoneId: e.target.value }))}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">No zone</option>
              {(zonesQ.data ?? []).map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eq-notes" className="font-mono text-[11px] uppercase tracking-wider">
              Notes
            </Label>
            <textarea
              id="eq-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>
          {error ? (
            <p className="text-sm text-warning font-medium" role="alert">
              {error}
            </p>
          ) : null}
          <div className="sticky bottom-0 -mx-5 px-5 pt-3 pb-1 bg-card border-t border-rule flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || patch.isPending} data-testid="eq-submit">
              {(create.isPending || patch.isPending) ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
